import React, { useState, useEffect } from 'react';
import { Card, Table, Button, message, Space, Tag, InputNumber, Typography, Switch, Input, Modal, Select, Popconfirm } from 'antd';
import { SaveOutlined, SettingOutlined, PlusOutlined, DeleteOutlined, ShareAltOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { Title, Text } = Typography;
const { Option } = Select;

interface GameType {
  id: number;
  name: string;
  display_name: string;
}

interface PricingInfo {
  id: number;
  game_type_id: number;
  max_players: number;
  billing_period: string;
  price: number;
  game_name: string;
  is_active: number; // 数据库中是 0 或 1
}

const ServerPricingManagement: React.FC = () => {
  const [pricingData, setPricingData] = useState<PricingInfo[]>([]);
  const [gameTypes, setGameTypes] = useState<GameType[]>([]);
  const [loading, setLoading] = useState(false);
  
  // 内联编辑状态
  const [editingData, setEditingData] = useState<{[key: string]: any}>({});
  const [enabledPeriods, setEnabledPeriods] = useState<{[key: string]: boolean}>({});
  const [saving, setSaving] = useState<{[key: string]: boolean}>({});
  
  // 增加行功能状态
  const [addRowModalVisible, setAddRowModalVisible] = useState(false);
  const [currentGameTypeId, setCurrentGameTypeId] = useState<number | null>(null);
  const [newMaxPlayers, setNewMaxPlayers] = useState<number>(20);
  const [insertPosition, setInsertPosition] = useState<'above' | 'below'>('below');
  const [insertAfterPlayer, setInsertAfterPlayer] = useState<number>(10);
  
  // 新增游戏类型价格表功能状态
  const [addGameModalVisible, setAddGameModalVisible] = useState(false);
  const [selectedGameTypeId, setSelectedGameTypeId] = useState<number | null>(null);
  const [newGamePricingData, setNewGamePricingData] = useState<{[key: string]: any}>({});
  const [newGameEnabledData, setNewGameEnabledData] = useState<{[key: string]: boolean}>({});
  const [newGameMaxPlayers, setNewGameMaxPlayers] = useState<number[]>([10, 30, 50]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [pricingResponse, gameTypesResponse] = await Promise.all([
        api.get('/admin/pricing'),
        api.get('/games/types')
      ]);
      setPricingData(pricingResponse.data);
      setGameTypes(gameTypesResponse.data);
      
      // 初始化编辑数据
      initializeEditingData(pricingResponse.data);
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  const initializeEditingData = (data: PricingInfo[]) => {
    const editData: {[key: string]: any} = {};
    const enabledData: {[key: string]: boolean} = {};
    
    data.forEach(item => {
      const key = `${item.game_type_id}_${item.max_players}_${item.billing_period}`;
      editData[key] = item.price;
      enabledData[key] = Boolean(item.is_active); // 将 0/1 转换为布尔值
    });
    
    setEditingData(editData);
    setEnabledPeriods(enabledData);
  };

  // 按游戏类型分组数据
  const groupedPricingData = pricingData.reduce((acc, item) => {
    const gameName = item.game_name;
    if (!acc[gameName]) {
      acc[gameName] = {
        gameTypeId: item.game_type_id,
        gameName: gameName,
        players: {}
      };
    }
    
    if (!acc[gameName].players[item.max_players]) {
      acc[gameName].players[item.max_players] = {};
    }
    
    acc[gameName].players[item.max_players][item.billing_period] = item;
    return acc;
  }, {} as any);

  // 付费周期配置
  const billingPeriods = [
    { key: 'weekly', label: '周付', color: 'blue' },
    { key: 'monthly', label: '月付', color: 'green' },
    { key: 'quarterly', label: '季度', color: 'orange' },
    { key: 'semiannual', label: '半年', color: 'purple' },
    { key: 'yearly', label: '年付', color: 'red' },
  ];

  // 最大玩家数选项（动态管理）
  const [maxPlayersOptions, setMaxPlayersOptions] = useState([10, 30, 50, 70, 127]);

  // 获取编辑数据的键
  const getDataKey = (gameTypeId: number, maxPlayers: number, billingPeriod: string) => {
    return `${gameTypeId}_${maxPlayers}_${billingPeriod}`;
  };

  // 检查某个付费周期是否全部启用
  const isPeriodAllEnabled = (periodKey: string) => {
    let allEnabled = true;
    let hasAnyConfig = false;
    
    maxPlayersOptions.forEach(players => {
      Object.keys(groupedPricingData).forEach(gameName => {
        const gameData = groupedPricingData[gameName];
        const key = getDataKey(gameData.gameTypeId, players, periodKey);
        const isEnabled = enabledPeriods[key] !== undefined ? enabledPeriods[key] : true;
        if (isEnabled !== undefined) {
          hasAnyConfig = true;
          if (!isEnabled) {
            allEnabled = false;
          }
        }
      });
    });
    
    return hasAnyConfig ? allEnabled : true;
  };

  // 检查某个付费周期是否全部禁用
  const isPeriodAllDisabled = (periodKey: string) => {
    let allDisabled = true;
    let hasAnyConfig = false;
    
    maxPlayersOptions.forEach(players => {
      Object.keys(groupedPricingData).forEach(gameName => {
        const gameData = groupedPricingData[gameName];
        const key = getDataKey(gameData.gameTypeId, players, periodKey);
        const isEnabled = enabledPeriods[key] !== undefined ? enabledPeriods[key] : true;
        if (isEnabled !== undefined) {
          hasAnyConfig = true;
          if (isEnabled) {
            allDisabled = false;
          }
        }
      });
    });
    
    return hasAnyConfig ? allDisabled : false;
  };

  // 检查新增游戏类型价格表中某个付费周期是否全部启用
  const isNewGamePeriodAllEnabled = (periodKey: string) => {
    if (!selectedGameTypeId) return true;
    
    let allEnabled = true;
    let hasAnyConfig = false;
    
    newGameMaxPlayers.forEach(players => {
      const key = getDataKey(selectedGameTypeId, players, periodKey);
      const isEnabled = newGameEnabledData[key] !== undefined ? newGameEnabledData[key] : true;
      hasAnyConfig = true;
      if (!isEnabled) {
        allEnabled = false;
      }
    });
    
    return hasAnyConfig ? allEnabled : true;
  };

  // 检查新增游戏类型价格表中某个付费周期是否全部禁用
  const isNewGamePeriodAllDisabled = (periodKey: string) => {
    if (!selectedGameTypeId) return false;
    
    let allDisabled = true;
    let hasAnyConfig = false;
    
    newGameMaxPlayers.forEach(players => {
      const key = getDataKey(selectedGameTypeId, players, periodKey);
      const isEnabled = newGameEnabledData[key] !== undefined ? newGameEnabledData[key] : true;
      hasAnyConfig = true;
      if (isEnabled) {
        allDisabled = false;
      }
    });
    
    return hasAnyConfig ? allDisabled : false;
  };

  // 处理价格变化
  const handlePriceChange = (gameTypeId: number, maxPlayers: number, billingPeriod: string, value: number | null) => {
    const key = getDataKey(gameTypeId, maxPlayers, billingPeriod);
    setEditingData(prev => ({
      ...prev,
      [key]: value || 0
    }));
  };

  // 处理启用状态变化
  const handleEnabledChange = (gameTypeId: number, maxPlayers: number, billingPeriod: string, enabled: boolean) => {
    const key = getDataKey(gameTypeId, maxPlayers, billingPeriod);
    setEnabledPeriods(prev => ({
      ...prev,
      [key]: enabled
    }));
    console.log(`单个配置 ${key} ${enabled ? '启用' : '禁用'}`);
  };

  // 处理最大玩家数变化
  const handleMaxPlayersChange = (gameTypeId: number, oldPlayers: number, newPlayers: number) => {
    const newEditData = { ...editingData };
    const newEnabledData = { ...enabledPeriods };
    
    // 为每个付费周期创建新的数据
    billingPeriods.forEach(period => {
      const oldKey = getDataKey(gameTypeId, oldPlayers, period.key);
      const newKey = getDataKey(gameTypeId, newPlayers, period.key);
      
      if (newEditData[oldKey] !== undefined) {
        newEditData[newKey] = newEditData[oldKey];
        newEnabledData[newKey] = newEnabledData[oldKey] || true;
        
        // 删除旧数据
        delete newEditData[oldKey];
        delete newEnabledData[oldKey];
      }
    });
    
    setEditingData(newEditData);
    setEnabledPeriods(newEnabledData);
  };

  // 保存单个游戏的所有配置
  const handleSaveGame = async (gameTypeId: number, gameName: string) => {
    const gameKey = `game_${gameTypeId}`;
    setSaving(prev => ({ ...prev, [gameKey]: true }));
    
    try {
      const updates: any[] = [];
      const creates: any[] = [];
      
      // 收集所有需要更新或创建的数据
      maxPlayersOptions.forEach(players => {
        billingPeriods.forEach(period => {
          const key = getDataKey(gameTypeId, players, period.key);
          const price = editingData[key];
          const enabled = enabledPeriods[key] !== undefined ? enabledPeriods[key] : true;
          
          const existingPricing = pricingData.find(p => 
            p.game_type_id === gameTypeId && 
            p.max_players === players && 
            p.billing_period === period.key
          );
          
          console.log(`处理配置: ${key}, 价格: ${price}, 启用: ${enabled}, 存在: ${!!existingPricing}`);
          
          if (existingPricing) {
            // 存在记录，需要更新（包括价格和启用/禁用状态）
            updates.push({
              id: existingPricing.id,
              game_type_id: gameTypeId,
              max_players: players,
              billing_period: period.key,
              price: price !== undefined ? price : existingPricing.price,
              is_active: enabled ? 1 : 0 // 转换为 0/1
            });
          } else if (price !== undefined && price > 0) {
            // 不存在记录但有价格，需要创建
            creates.push({
              game_type_id: gameTypeId,
              max_players: players,
              billing_period: period.key,
              price: price,
              is_active: enabled ? 1 : 0 // 转换为 0/1
            });
          }
        });
      });
      
      console.log(`保存操作: 更新${updates.length}条, 创建${creates.length}条`);
      
      // 执行批量操作
      const promises: Promise<any>[] = [];
      
      // 更新现有记录
      updates.forEach(update => {
        promises.push(api.put(`/admin/pricing/${update.id}`, update));
      });
      
      // 创建新记录
      creates.forEach(create => {
        promises.push(api.post('/admin/pricing', create));
      });
      
      await Promise.all(promises);
      
      message.success(`${gameName} 价格配置保存成功`);
      loadData(); // 重新加载数据
    } catch (error: any) {
      console.error('保存失败:', error);
      message.error(error.response?.data?.message || '保存失败');
    } finally {
      setSaving(prev => ({ ...prev, [gameKey]: false }));
    }
  };

  // 处理增加行
  const handleAddRow = (gameTypeId: number) => {
    setCurrentGameTypeId(gameTypeId);
    setNewMaxPlayers(20);
    setInsertPosition('below');
    setInsertAfterPlayer(10);
    setAddRowModalVisible(true);
  };

  // 确认增加行
  const handleConfirmAddRow = () => {
    if (!currentGameTypeId || newMaxPlayers <= 0) {
      message.error('请输入有效的最大玩家数');
      return;
    }

    // 判断是新增游戏类型价格表还是现有游戏类型价格表
    if (selectedGameTypeId && currentGameTypeId === selectedGameTypeId) {
      // 新增游戏类型价格表的增加行
      handleConfirmAddNewGameRow();
    } else {
      // 现有游戏类型价格表的增加行
      if (maxPlayersOptions.includes(newMaxPlayers)) {
        message.error('该玩家数已存在');
        return;
      }

      // 计算插入位置
      let newMaxPlayersOptions = [...maxPlayersOptions];
      const insertIndex = newMaxPlayersOptions.findIndex(players => players === insertAfterPlayer);
      
      if (insertIndex === -1) {
        message.error('找不到参考位置');
        return;
      }

      // 插入新玩家数
      const targetIndex = insertPosition === 'above' ? insertIndex : insertIndex + 1;
      newMaxPlayersOptions.splice(targetIndex, 0, newMaxPlayers);
      
      setMaxPlayersOptions(newMaxPlayersOptions);

      // 为新玩家数初始化编辑数据
      const newEditData = { ...editingData };
      const newEnabledData = { ...enabledPeriods };
      
      billingPeriods.forEach(period => {
        const key = getDataKey(currentGameTypeId, newMaxPlayers, period.key);
        newEditData[key] = 0; // 默认价格为0
        newEnabledData[key] = true; // 默认启用
      });
      
      setEditingData(newEditData);
      setEnabledPeriods(newEnabledData);
      
      setAddRowModalVisible(false);
      message.success(`已添加 ${newMaxPlayers}人 配置行`);
    }
  };

  // 删除行
  const handleDeleteRow = (gameTypeId: number, maxPlayers: number) => {
    // 从最大玩家数选项中移除
    const newMaxPlayersOptions = maxPlayersOptions.filter(players => players !== maxPlayers);
    setMaxPlayersOptions(newMaxPlayersOptions);

    // 清理相关的编辑数据
    const newEditData = { ...editingData };
    const newEnabledData = { ...enabledPeriods };
    
    billingPeriods.forEach(period => {
      const key = getDataKey(gameTypeId, maxPlayers, period.key);
      delete newEditData[key];
      delete newEnabledData[key];
    });
    
    setEditingData(newEditData);
    setEnabledPeriods(newEnabledData);
    
    message.success(`已删除 ${maxPlayers}人 配置行`);
  };

  // 处理新增游戏类型价格表
  const handleAddGameType = () => {
    setSelectedGameTypeId(null);
    setNewGamePricingData({});
    setNewGameEnabledData({});
    setAddGameModalVisible(true);
  };

  // 确认新增游戏类型价格表
  const handleConfirmAddGameType = () => {
    if (!selectedGameTypeId) {
      message.error('请选择游戏类型');
      return;
    }

    // 检查是否已存在该游戏类型的价格配置
    const existingGame = Object.keys(groupedPricingData).find(gameName => {
      const gameData = groupedPricingData[gameName];
      return gameData.gameTypeId === selectedGameTypeId;
    });

    if (existingGame) {
      message.error('该游戏类型已存在价格配置');
      return;
    }

    // 初始化新游戏的价格数据（默认3行：10人、30人、50人）
    const defaultMaxPlayers = [10, 30, 50];
    const newPricingData: {[key: string]: any} = {};
    const newEnabledData: {[key: string]: boolean} = {};

    defaultMaxPlayers.forEach(players => {
      billingPeriods.forEach(period => {
        const key = getDataKey(selectedGameTypeId, players, period.key);
        newPricingData[key] = 0; // 默认价格为0
        newEnabledData[key] = true; // 默认启用
      });
    });

    setNewGamePricingData(newPricingData);
    setNewGameEnabledData(newEnabledData);
    setAddGameModalVisible(false);
    message.success('已创建新游戏类型价格表，请设置价格后保存');
  };

  // 保存新游戏类型的价格配置
  const handleSaveNewGameType = async () => {
    if (!selectedGameTypeId) {
      message.error('游戏类型ID不存在');
      return;
    }

    try {
      const creates: any[] = [];
      
      newGameMaxPlayers.forEach(players => {
        billingPeriods.forEach(period => {
          const key = getDataKey(selectedGameTypeId, players, period.key);
          const price = newGamePricingData[key];
          const enabled = newGameEnabledData[key] !== undefined ? newGameEnabledData[key] : true;
          
          if (price !== undefined && price > 0) {
            creates.push({
              game_type_id: selectedGameTypeId,
              max_players: players,
              billing_period: period.key,
              price: price,
              is_active: enabled ? 1 : 0 // 转换为 0/1
            });
          }
        });
      });

      if (creates.length === 0) {
        message.warning('请至少设置一个价格');
        return;
      }

      // 批量创建价格配置
      const promises = creates.map(create => api.post('/admin/pricing', create));
      await Promise.all(promises);

      message.success('新游戏类型价格配置保存成功');
      
      // 重置状态
      setNewGamePricingData({});
      setNewGameEnabledData({});
      setNewGameMaxPlayers([10, 30, 50]);
      setSelectedGameTypeId(null);
      
      // 重新加载数据
      loadData();
    } catch (error: any) {
      console.error('保存新游戏类型价格配置失败:', error);
      message.error(error.response?.data?.message || '保存失败');
    }
  };

  // 处理新增游戏类型价格表增加行
  const handleAddNewGameRow = () => {
    setCurrentGameTypeId(selectedGameTypeId);
    setNewMaxPlayers(20);
    setInsertPosition('below');
    setInsertAfterPlayer(newGameMaxPlayers[newGameMaxPlayers.length - 1] || 10);
    setAddRowModalVisible(true);
  };

  // 确认新增游戏类型价格表增加行
  const handleConfirmAddNewGameRow = () => {
    if (!selectedGameTypeId || !currentGameTypeId || newMaxPlayers <= 0) {
      message.error('请输入有效的最大玩家数');
      return;
    }

    if (newGameMaxPlayers.includes(newMaxPlayers)) {
      message.error('该玩家数已存在');
      return;
    }

    // 计算插入位置
    let newMaxPlayersList = [...newGameMaxPlayers];
    const insertIndex = newMaxPlayersList.findIndex(players => players === insertAfterPlayer);
    
    if (insertIndex === -1) {
      message.error('找不到参考位置');
      return;
    }

    // 插入新玩家数
    const targetIndex = insertPosition === 'above' ? insertIndex : insertIndex + 1;
    newMaxPlayersList.splice(targetIndex, 0, newMaxPlayers);
    
    setNewGameMaxPlayers(newMaxPlayersList);

    // 为新玩家数初始化编辑数据
    const newPricingData = { ...newGamePricingData };
    const newEnabledData = { ...newGameEnabledData };
    
    billingPeriods.forEach(period => {
      const key = getDataKey(selectedGameTypeId, newMaxPlayers, period.key);
      newPricingData[key] = 0; // 默认价格为0
      newEnabledData[key] = true; // 默认启用
    });
    
    setNewGamePricingData(newPricingData);
    setNewGameEnabledData(newEnabledData);
    
    setAddRowModalVisible(false);
    message.success(`已添加 ${newMaxPlayers}人 配置行`);
  };

  return (
    <div className="fade-in">
      <Card>
        <div className="mb-6">
          <div className="flex justify-between items-center">
            <div>
              <Title level={2} className="mb-2">服务器价格管理</Title>
              <Text type="secondary">按游戏类型管理不同配置的服务器价格，支持内联编辑</Text>
            </div>
            <div className="flex gap-2">
              <Button
                type="default"
                icon={<ShareAltOutlined />}
                onClick={() => window.open('/pricing', '_blank')}
                size="large"
              >
                查看公开价格表
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddGameType}
                size="large"
              >
                新增游戏类型价格表
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <Text>加载中...</Text>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.keys(groupedPricingData).length === 0 ? (
              <div className="text-center py-12">
                <SettingOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />
                <div className="mt-4">
                  <Text type="secondary">暂无价格配置</Text>
                </div>
              </div>
            ) : (
              Object.entries(groupedPricingData).map(([gameName, gameData]: [string, any]) => (
                <Card key={gameName} className="mb-4">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <Title level={4} className="mb-2">{gameName}</Title>
                      <Text type="secondary">配置不同玩家数和付费周期的价格</Text>
                    </div>
          <Button
            type="primary"
                      icon={<SaveOutlined />}
                      onClick={() => handleSaveGame(gameData.gameTypeId, gameName)}
                      loading={saving[`game_${gameData.gameTypeId}`]}
                      size="large"
                    >
                      保存配置
          </Button>
        </div>

        <Table
                    dataSource={maxPlayersOptions.map(players => ({
                      key: players,
                      players: players,
                      gameTypeId: gameData.gameTypeId
                    }))}
                    pagination={false}
                    size="small"
                    bordered
                  >
                    <Table.Column
                      title={<div className="text-center">最大玩家数</div>}
                      dataIndex="players"
                      key="players"
                      width={160}
                      render={(players: number, record: any) => (
                        <div className="flex items-center justify-center gap-2">
                          <Input
                            value={players}
                            onChange={(e) => {
                              const newPlayers = parseInt(e.target.value) || players;
                              if (newPlayers !== players) {
                                handleMaxPlayersChange(record.gameTypeId, players, newPlayers);
                              }
                            }}
                            style={{ textAlign: 'center', fontWeight: 'bold', width: '80px' }}
                            size="small"
                          />
                          <Popconfirm
                            title="确定要删除这个配置行吗？"
                            description="删除后将无法恢复，请谨慎操作。"
                            onConfirm={() => handleDeleteRow(record.gameTypeId, players)}
                            okText="确定"
                            cancelText="取消"
                          >
                            <Button
                              type="text"
                              danger
                              size="small"
                              icon={<DeleteOutlined />}
                              style={{ padding: '2px 4px' }}
                            />
                          </Popconfirm>
                        </div>
                      )}
                    />
                    {billingPeriods.map(period => (
                      <Table.Column
                        key={period.key}
                        title={
                          <div className="text-center">
                            <div className="mb-1">
                              <Tag color={period.color}>{period.label}</Tag>
                            </div>
                            <div className="flex items-center justify-center gap-1">
                              <Switch
                                size="small"
                                checked={isPeriodAllEnabled(period.key)}
                                onChange={(checked) => {
                                  // 批量启用/禁用该付费周期下的所有价格配置
                                  const newEnabledData = { ...enabledPeriods };
                                  maxPlayersOptions.forEach(players => {
                                    Object.keys(groupedPricingData).forEach(gameName => {
                                      const gameData = groupedPricingData[gameName];
                                      const key = getDataKey(gameData.gameTypeId, players, period.key);
                                      newEnabledData[key] = checked;
                                    });
                                  });
                                  setEnabledPeriods(newEnabledData);
                                  console.log(`批量${checked ? '启用' : '禁用'} ${period.label} 周期下的所有配置`);
                                }}
                                style={{
                                  opacity: isPeriodAllEnabled(period.key) ? 1 : isPeriodAllDisabled(period.key) ? 0.5 : 0.8
                                }}
                              />
                              <span className="text-xs text-gray-500">
                                {isPeriodAllEnabled(period.key) ? '全启用' : isPeriodAllDisabled(period.key) ? '全禁用' : '部分启用'}
                              </span>
                            </div>
                          </div>
                        }
                        dataIndex={period.key}
                        width={220}
                        render={(pricing: PricingInfo, record: any) => {
                          const key = getDataKey(record.gameTypeId, record.players, period.key);
                          const currentPrice = editingData[key] !== undefined ? editingData[key] : (pricing?.price || 0);
                          const isEnabled = enabledPeriods[key] !== undefined ? enabledPeriods[key] : true;
                          
                          return (
                            <div className="text-center p-2">
                              <div className="flex items-center justify-center gap-2">
                            <InputNumber
                              value={currentPrice}
                              onChange={(value) => handlePriceChange(record.gameTypeId, record.players, period.key, value)}
                              precision={2}
                              min={0}
                              addonBefore="¥"
                              size="small"
                              style={{ 
                                width: 'auto',
                                minWidth: '80px',
                                maxWidth: '110px'
                              }}
                              disabled={!isEnabled}
                            />
                                <Switch
                                  size="small"
                                  checked={isEnabled}
                                  onChange={(checked) => handleEnabledChange(record.gameTypeId, record.players, period.key, checked)}
                                  checkedChildren="启用"
                                  unCheckedChildren="禁用"
                                />
                              </div>
                            </div>
                          );
                        }}
                      />
                    ))}
                  </Table>
                  
                  {/* 增加行按钮 */}
                  <div className="mt-4 text-center">
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => handleAddRow(gameData.gameTypeId)}
                      size="large"
                    >
                      增加配置行
                    </Button>
                  </div>
                </Card>
              ))
            )}
          </div>
        )}
      </Card>

      {/* 增加行模态框 */}
        <Modal
        title="增加配置行"
        open={addRowModalVisible}
        onCancel={() => setAddRowModalVisible(false)}
        onOk={handleConfirmAddRow}
        width={500}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">最大玩家数</label>
            <InputNumber
              value={newMaxPlayers}
              onChange={(value) => setNewMaxPlayers(value || 20)}
              min={1}
              max={1000}
              style={{ width: '100%' }}
              placeholder="输入最大玩家数"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">插入位置</label>
            <div className="flex items-center space-x-4">
              <Select
                value={insertAfterPlayer}
                onChange={(value) => setInsertAfterPlayer(value)}
                style={{ width: 120 }}
              >
                {(selectedGameTypeId && currentGameTypeId === selectedGameTypeId ? newGameMaxPlayers : maxPlayersOptions).map(players => (
                  <Option key={players} value={players}>
                    {players}人
                  </Option>
                ))}
              </Select>
              <Select
                value={insertPosition}
                onChange={(value) => setInsertPosition(value)}
                style={{ width: 100 }}
              >
                <Option value="above">上方</Option>
                <Option value="below">下方</Option>
              </Select>
            </div>
            <Text type="secondary" className="text-xs mt-1">
              将在 {insertAfterPlayer}人 配置的{insertPosition === 'above' ? '上方' : '下方'}插入新的 {newMaxPlayers}人 配置
            </Text>
          </div>
        </div>
      </Modal>

      {/* 新增游戏类型价格表模态框 */}
      <Modal
        title="新增游戏类型价格表"
        open={addGameModalVisible}
        onCancel={() => setAddGameModalVisible(false)}
        onOk={handleConfirmAddGameType}
        width={500}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">选择游戏类型</label>
            <Select
              value={selectedGameTypeId}
              onChange={(value) => setSelectedGameTypeId(value)}
              style={{ width: '100%' }}
              placeholder="请选择游戏类型"
            >
              {gameTypes.map(gameType => (
                <Option key={gameType.id} value={gameType.id}>
                  {gameType.display_name}
                  </Option>
                ))}
              </Select>
          </div>
          <Text type="secondary" className="text-xs">
            将创建包含10人、30人、50人三种配置的价格表，您可以在创建后设置具体价格
          </Text>
        </div>
      </Modal>

      {/* 新游戏类型价格表编辑区域 */}
      {selectedGameTypeId && Object.keys(newGamePricingData).length > 0 && (
        <Card className="mt-6" style={{ border: '2px dashed #1890ff' }}>
          <div className="mb-4">
            <div className="flex justify-between items-center">
              <div>
                <Title level={3} className="mb-2">
                  新游戏类型价格配置 - {gameTypes.find(gt => gt.id === selectedGameTypeId)?.display_name}
                </Title>
                <Text type="secondary">配置不同玩家数和付费周期的价格</Text>
              </div>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSaveNewGameType}
                size="large"
              >
                保存配置
              </Button>
            </div>
          </div>

          <Table
            dataSource={newGameMaxPlayers.map(players => ({
              key: players,
              players: players,
              gameTypeId: selectedGameTypeId
            }))}
            pagination={false}
            size="small"
            bordered
          >
            <Table.Column
              title={<div className="text-center">最大玩家数</div>}
              dataIndex="players"
              key="players"
              width={160}
              render={(players: number, record: any) => (
                <div className="flex items-center justify-center gap-2">
                  <Input
                    value={players}
                    style={{ textAlign: 'center', fontWeight: 'bold', width: '80px' }}
                    size="small"
                    readOnly
                  />
                </div>
              )}
            />
            {billingPeriods.map(period => (
              <Table.Column
                key={period.key}
                title={
                  <div className="text-center">
                    <div className="mb-1">
                      <Tag color={period.color}>{period.label}</Tag>
                    </div>
                    <div className="flex items-center justify-center gap-1">
                      <Switch
                        size="small"
                        checked={isNewGamePeriodAllEnabled(period.key)}
                        onChange={(checked) => {
                          // 批量启用/禁用该付费周期下的所有价格配置
                          const newEnabledData = { ...newGameEnabledData };
                          newGameMaxPlayers.forEach(players => {
                            const key = getDataKey(selectedGameTypeId!, players, period.key);
                            newEnabledData[key] = checked;
                          });
                          setNewGameEnabledData(newEnabledData);
                          console.log(`批量${checked ? '启用' : '禁用'} ${period.label} 周期下的所有配置`);
                        }}
                        style={{
                          opacity: isNewGamePeriodAllEnabled(period.key) ? 1 : isNewGamePeriodAllDisabled(period.key) ? 0.5 : 0.8
                        }}
                      />
                      <span className="text-xs text-gray-500">
                        {isNewGamePeriodAllEnabled(period.key) ? '全启用' : isNewGamePeriodAllDisabled(period.key) ? '全禁用' : '部分启用'}
                      </span>
                    </div>
                  </div>
                }
                dataIndex={period.key}
                width={220}
                render={(pricing: PricingInfo, record: any) => {
                  const key = getDataKey(record.gameTypeId, record.players, period.key);
                  const currentPrice = newGamePricingData[key] !== undefined ? newGamePricingData[key] : 0;
                  const isEnabled = newGameEnabledData[key] !== undefined ? newGameEnabledData[key] : true;
                  
                  return (
                    <div className="text-center p-2">
                      <div className="flex items-center justify-center gap-2">
                        <InputNumber
                          value={currentPrice}
                          onChange={(value) => {
                            setNewGamePricingData(prev => ({
                              ...prev,
                              [key]: value || 0
                            }));
                          }}
                precision={2}
                min={0}
                addonBefore="¥"
                          size="small"
                          style={{ 
                            width: 'auto',
                            minWidth: '80px',
                            maxWidth: '110px'
                          }}
                          disabled={!isEnabled}
                        />
                        <Switch
                          size="small"
                          checked={isEnabled}
                          onChange={(checked) => {
                            setNewGameEnabledData(prev => ({
                              ...prev,
                              [key]: checked
                            }));
                          }}
                          checkedChildren="启用"
                          unCheckedChildren="禁用"
                        />
                      </div>
                    </div>
                  );
                }}
              />
            ))}
          </Table>
          
          {/* 增加行按钮 */}
          <div className="mt-4 text-center">
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={handleAddNewGameRow}
              size="large"
            >
              增加配置行
                </Button>
              </div>
      </Card>
      )}
    </div>
  );
};

export default ServerPricingManagement;